import { useContext, useState, useEffect, useMemo } from 'react';
import {
  ListSubheader,
  alpha,
  Box,
  List,
  styled,
  Button,
  ListItem,
  Collapse,
  Skeleton,
  useTheme
} from '@mui/material';
import { NavLink as RouterLink } from 'react-router-dom';
import { SidebarContext } from 'src/contexts/SidebarContext';
import BrightnessLowTwoToneIcon from '@mui/icons-material/BrightnessLowTwoTone';
import TableChartTwoToneIcon from '@mui/icons-material/TableChartTwoTone';
import MessageIcon from '@mui/icons-material/Message';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import PersonIcon from '@mui/icons-material/Person';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import ApartmentIcon from '@mui/icons-material/Apartment';
import LogoutIcon from '@mui/icons-material/Logout';
import GroupIcon from '@mui/icons-material/Group';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import MarkEmailUnreadRoundedIcon from '@mui/icons-material/MarkEmailUnreadRounded';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import GroupsIcon from '@mui/icons-material/Groups';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ForumIcon from '@mui/icons-material/Forum';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import WorkIcon from '@mui/icons-material/Work';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import RoomServiceOutlinedIcon from '@mui/icons-material/RoomServiceOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../../../redux/authSlice';
import { incrementCollabUnread, fetchSentCollaborations } from '../../../../redux/collaborationSlice';
import api from '../../../../redux/config';
import { useTranslation } from 'react-i18next';
import { collabSocket } from '../../../../socket/collabSocket';
import { notificationsSocket } from '../../../../socket/notificationsSocket';
import { socket as claimSocket } from '../../../../socket/config';
import { fetchPendingServiceCount } from '../../../../redux/serviceRequestSlice';
import { isHotelBusinessArea } from 'src/utils/businessArea';
import { usePackFeatures } from 'src/contexts/PackFeaturesContext';
import { PACK_FEATURE } from 'src/utils/packFeatures';

const MenuWrapper = styled(Box)(
  ({ theme }) => `
  .MuiList-root {
    padding: ${theme.spacing(1)};

    & > .MuiList-root {
      padding: 0 ${theme.spacing(0)} ${theme.spacing(1)};
    }
  }

  .MuiListSubheader-root {
    text-transform: uppercase;
    font-weight: bold;
    font-size: ${theme.typography.pxToRem(12)};
    color: ${theme.sidebar.ink[50]};
    padding: ${theme.spacing(0, 2.5)};
    line-height: 1.4;
  }
`
);

const SubMenuWrapper = styled(Box)(
  ({ theme }) => `
    .MuiList-root {
      .MuiListItem-root {
        padding: 0.2px 0;

        .MuiBadge-root {
          position: absolute;
          right: ${theme.spacing(3.2)};

          .MuiBadge-standard {
            background: ${theme.colors.primary.main};
            font-size: ${theme.typography.pxToRem(10)};
            font-weight: bold;
            text-transform: uppercase;
            color: ${theme.palette.primary.contrastText};
          }
        }

        .MuiButton-root {
          display: flex;
          color: ${theme.sidebar.ink[70]};
          background-color: transparent;
          width: 100%;
          justify-content: flex-start;
          padding: ${theme.spacing(1.2, 3)};

          .MuiButton-startIcon,
          .MuiButton-endIcon {
            transition: ${theme.transitions.create(['color'])};

            .MuiSvgIcon-root {
              font-size: inherit;
              transition: none;
            }
          }

          .MuiButton-startIcon {
            color: ${theme.sidebar.ink[30]};
            font-size: ${theme.typography.pxToRem(20)};
            margin-right: ${theme.spacing(1)};
          }

          .MuiButton-endIcon {
            color: ${theme.sidebar.ink[50]};
            margin-left: auto;
            opacity: .8;
            font-size: ${theme.typography.pxToRem(20)};
          }

          &.active,
          &:hover {
            background-color: ${alpha(theme.sidebar.ink[100], 0.06)};
            color: ${theme.sidebar.ink[100]};

            .MuiButton-startIcon,
            .MuiButton-endIcon {
              color: ${theme.sidebar.ink[100]};
            }
          }
        }

        &.Mui-children {
          flex-direction: column;

          .MuiBadge-root {
            position: absolute;
            right: ${theme.spacing(7)};
          }
        }

        .MuiCollapse-root {
          width: 100%;

          .MuiList-root {
            padding: ${theme.spacing(1, 0)};
          }

          .MuiListItem-root {
            padding: 1px 0;

            .MuiButton-root {
              padding: ${theme.spacing(0.8, 3)};

              .MuiBadge-root {
                right: ${theme.spacing(3.2)};
              }

              &:before {
                content: ' ';
                background: ${theme.sidebar.ink[100]};
                opacity: 0;
                transition: ${theme.transitions.create([
                  'transform',
                  'opacity'
                ])};
                width: 6px;
                height: 6px;
                transform: scale(0);
                transform-origin: center;
                border-radius: 20px;
                margin-right: ${theme.spacing(1.8)};
              }

              &.active,
              &:hover {

                &:before {
                  transform: scale(1);
                  opacity: 1;
                }
              }
            }
          }
        }
      }
    }
`
);

function SidebarMenu() {
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const role = useSelector((x) => x.user.role);
  const profile = useSelector((s) => s.user.profile);
  const isHotel = isHotelBusinessArea(profile);
  const serviceRequestsEnabled = profile?.company?.serviceRequestsEnabled ?? true;
  const collaborations = useSelector((s) =>
    Array.isArray(s.collaborations?.collaborations) ? s.collaborations.collaborations : []
  );
  const pendingServiceCount = useSelector((s) => s.serviceRequests?.pendingCount || 0);
  const { closeSidebar } = useContext(SidebarContext);
  // Menus des fonctionnalités incluses dans le pack de l'entreprise (tout, tant que la
  // vérification des packs est désactivée côté back-end), affichés une fois le pack connu
  // pour ne pas montrer un instant des menus qui disparaissent. Les badges des
  // fonctionnalités absentes ne sont pas chargés : leurs routes répondraient 403.
  const { hasFeature, loaded: packLoaded } = usePackFeatures();
  const canClaims = packLoaded && hasFeature(PACK_FEATURE.CLAIMS);
  const canReservations = packLoaded && hasFeature(PACK_FEATURE.RESERVATIONS);
  const canStaff = packLoaded && hasFeature(PACK_FEATURE.STAFF_MANAGEMENT);
  const canStaffReviews = packLoaded && hasFeature(PACK_FEATURE.STAFF_REVIEWS);
  const [open, setOpen] = useState(false);
  const [open1, setOpen1] = useState(false);
  const [open2, setOpen2] = useState(false);
  const [pendingCollabCount, setPendingCollabCount] = useState(0);
  const [claimsUnreadCount, setClaimsUnreadCount] = useState(0);

  const totalUnread = collaborations.reduce((sum, c) => sum + (c.adminUnreadCount || 0), 0);

  const pendingRequests = useMemo(() => {
    let count = 0;
    collaborations.forEach((c) => {
      ['disease', 'absence', 'dayOff', 'end'].forEach((type) => {
        count += (c[type] || []).filter((i) => i.status === 'Pending').length;
      });
    });
    return count;
  }, [collaborations]);

  useEffect(() => {
    if (canStaff) dispatch(fetchSentCollaborations());
  }, [dispatch, canStaff]);

  // Pending received-collaborations badge (job applications awaiting review).
  // Fetched directly via api instead of the collaborations redux slice
  // because fetchReciveidCollaborations writes into the same state.collaborations
  // field used above for sent-collaboration counts.
  // Backend emits 'notificationAdded' (kind: 'newApplication') on the
  // /collaboration socket namespace when a candidate applies
  // (collaboration.service.ts add() -> sendDirectNotif -> handleNotificationAdded),
  // so we refetch on that event for a live update. Accept/reject on this
  // endpoint (acceptCollaborationSent/rejectCollaborationsent) emit no socket
  // event, so refetching on mount covers those once the admin navigates back.
  useEffect(() => {
    if (role !== 'ADMIN' || !canStaff) return undefined;
    const fetchCount = () => {
      api
        .get('/collaboration/recived')
        .then((response) => {
          const data = response.data?.data || response.data || [];
          const list = Array.isArray(data) ? data : [];
          setPendingCollabCount(
            list.filter((c) => c.status === 'Pending').length
          );
        })
        .catch((error) => {
          console.error('Error fetching received collaborations count:', error);
        });
    };
    fetchCount();
    const handleNewApplication = (payload) => {
      if (payload?.notification?.kind === 'newApplication') fetchCount();
    };
    collabSocket.on('notificationAdded', handleNewApplication);
    window.addEventListener('collab:pendingChanged', fetchCount);
    return () => {
      collabSocket.off('notificationAdded', handleNewApplication);
      window.removeEventListener('collab:pendingChanged', fetchCount);
    };
  }, [role, canStaff]);

  // Pending waiter-call / room-service badge: load the count on mount and
  // keep it fresh via the company-room socket event. The count endpoint is
  // ADMIN/MANAGER-only, so skip the fetch for other roles.
  useEffect(() => {
    const companyId = profile?.company?._id;
    if (!companyId || !(role === 'ADMIN' || role === 'MANAGER') || !canReservations) return undefined;
    const fetchCount = () => dispatch(fetchPendingServiceCount({ companyId, isHotel }));
    fetchCount();
    const event = isHotel ? 'room-service:requested' : 'waiter:called';
    notificationsSocket.on(event, fetchCount);
    return () => {
      notificationsSocket.off(event, fetchCount);
    };
  }, [dispatch, profile, role, isHotel, canReservations]);

  // Unread client messages on claims ("Messages" badge). Refetched when a
  // claim message arrives ('notif-message' on the claim socket) and when a
  // conversation is marked seen (markClaimMessagesSeen fires
  // 'claims:unreadChanged').
  useEffect(() => {
    if (!role || !canClaims) return undefined;
    const fetchCount = () => {
      api
        .get('/claim/unread-count')
        .then((response) => setClaimsUnreadCount(response.data?.count || 0))
        .catch((error) => {
          console.error('Error fetching claims unread count:', error);
        });
    };
    fetchCount();
    claimSocket.on('notif-message', fetchCount);
    window.addEventListener('claims:unreadChanged', fetchCount);
    return () => {
      claimSocket.off('notif-message', fetchCount);
      window.removeEventListener('claims:unreadChanged', fetchCount);
    };
  }, [role, canClaims]);

  useEffect(() => {
    const handler = (payload) => {
      const collabId = payload.id || payload.message?.chat || payload.file?.chat;
      if (collabId) dispatch(incrementCollabUnread(collabId));
    };
    collabSocket.on('newMessage', handler);
    collabSocket.on('newFile', handler);
    return () => {
      collabSocket.off('newMessage', handler);
      collabSocket.off('newFile', handler);
    };
  }, [dispatch]);

  const handleLogout = () => {
    dispatch(logout());
  };

  const handleClick = () => {
    setOpen(!open);
  };

  const handleClick1 = () => {
    setOpen1(!open1);
  };

  const handleClick2 = () => {
    setOpen2(!open2);
  };

  // Tant que le pack n'est pas connu, le menu complet n'est pas encore décidable : on
  // affiche des emplacements plutôt qu'un menu partiel qui se compléterait ensuite.
  if (!packLoaded) {
    return (
      <MenuWrapper aria-busy="true">
        {[3, 4, 3].map((rows, section) => (
          <Box key={section} sx={{ px: 2.5, pt: 2, pb: 1 }}>
            <Skeleton
              variant="text"
              width={90}
              sx={{ bgcolor: theme.sidebar.ink[10], mb: 1 }}
            />
            {Array.from({ length: rows }).map((_, i) => (
              <Box key={i} display="flex" alignItems="center" gap={1.5} sx={{ py: 1, pl: 1 }}>
                <Skeleton variant="circular" width={22} height={22} sx={{ bgcolor: theme.sidebar.ink[10] }} />
                <Skeleton variant="text" width={`${55 + ((i * 17) % 30)}%`} sx={{ bgcolor: theme.sidebar.ink[10] }} />
              </Box>
            ))}
          </Box>
        ))}
      </MenuWrapper>
    );
  }

  return (
    <>
      <MenuWrapper>
        <List
          component="div"
          subheader={
            <ListSubheader component="div" disableSticky>
              {t('sidebar.dashboard')}
            </ListSubheader>
          }
        >
          <SubMenuWrapper>
            <List component="div">
              <ListItem component="div" id="general">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/dashboards/tasks"
                  startIcon={
                    <BrightnessLowTwoToneIcon style={{ color: theme.colors.primary.main }} />
                  }
                >
                  {t('sidebar.general')}
                </Button>
              </ListItem>
            </List>
          </SubMenuWrapper>
        </List>
        <List
          component="div"
          subheader={
            <ListSubheader component="div" disableSticky>
              {t('sidebar.accounts')}
            </ListSubheader>
          }
        >
          <SubMenuWrapper>
            <List component="div">
              <ListItem component="div" id="profile">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/management/profile/settings"
                  startIcon={<AccountBoxIcon style={{ color: theme.colors.primary.main }} />}
                >
                  {t('sidebar.profile')}
                </Button>
              </ListItem>
              <ListItem component="div" id="company">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/management/company/settings"
                  startIcon={<ApartmentIcon style={{ color: theme.colors.primary.main }} />}
                >
                  {t('sidebar.company')}
                </Button>
              </ListItem>
              <ListItem component="div" id="lost-items">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/management/company/lost-items"
                  startIcon={
                    <Inventory2OutlinedIcon style={{ color: theme.colors.primary.main }} />
                  }
                >
                  {t('sidebar.lost_items')}
                </Button>
              </ListItem>
              {!isHotel && (
                <ListItem component="div" id="restaurant-menu">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/company/restaurant-menu"
                    startIcon={
                      <RestaurantMenuIcon style={{ color: theme.colors.primary.main }} />
                    }
                  >
                    {t('sidebar.restaurant_menu')}
                  </Button>
                </ListItem>
              )}
              {canReservations && (
              <ListItem component="div" id="reservations">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/management/company/reservations"
                  startIcon={
                    <EventAvailableIcon style={{ color: theme.colors.primary.main }} />
                  }
                >
                  {t('sidebar.reservations')}
                </Button>
              </ListItem>
              )}
              {canReservations && serviceRequestsEnabled && (
                <ListItem component="div" id="service-requests">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/company/service-requests"
                    startIcon={
                      isHotel ? (
                        <RoomServiceOutlinedIcon style={{ color: theme.colors.primary.main }} />
                      ) : (
                        <NotificationsActiveOutlinedIcon style={{ color: theme.colors.primary.main }} />
                      )
                    }
                  >
                    {isHotel
                      ? t('sidebar.room_service')
                      : t('sidebar.waiter_calls')}
                    {pendingServiceCount > 0 && (
                      <Box
                        component="span"
                        sx={{
                          ml: 1.5,
                          background: '#18e68e',
                          color: '#000',
                          borderRadius: '10px',
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          minWidth: 20,
                          height: 20,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          px: 0.8
                        }}
                      >
                        {pendingServiceCount}
                      </Box>
                    )}
                  </Button>
                </ListItem>
              )}
              {(role === 'ADMIN' || role === 'MANAGER') && (
                <ListItem component="div" id="favorite-clients">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/company/favorite-clients"
                    startIcon={
                      <FavoriteRoundedIcon style={{ color: theme.colors.primary.main }} />
                    }
                  >
                    {t('sidebar.favorite_clients')}
                  </Button>
                </ListItem>
              )}
            </List>
          </SubMenuWrapper>
        </List>
        {canClaims && (
        <List
          component="div"
          subheader={
            <ListSubheader component="div" disableSticky>
              {t('sidebar.management')}
            </ListSubheader>
          }
        >
          <SubMenuWrapper>
            <List component="div">
              <ListItem component="div" id="claims">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/management/claims"
                  startIcon={
                    <TableChartTwoToneIcon style={{ color: theme.colors.primary.main }} />
                  }
                >
                  {t('sidebar.claims')}
                </Button>
              </ListItem>
              <ListItem component="div" id="claimshistory">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/management/claimsHistory"
                  startIcon={<UnarchiveIcon style={{ color: theme.colors.primary.main }} />}
                >
                  {t('sidebar.claims_history')}
                </Button>
              </ListItem>
              <ListItem component="div" id="claimsMessanger">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/management/messenger"
                  startIcon={<MessageIcon style={{ color: theme.colors.primary.main }} />}
                >
                  {t('sidebar.Messages')}
                  {claimsUnreadCount > 0 && (
                    <Box
                      component="span"
                      sx={{
                        ml: 1.5,
                        background: '#18e68e',
                        color: '#000',
                        borderRadius: '10px',
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        minWidth: 20,
                        height: 20,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: 0.8
                      }}
                    >
                      {claimsUnreadCount}
                    </Box>
                  )}
                </Button>
              </ListItem>
            </List>
          </SubMenuWrapper>
        </List>
        )}

        {role !== 'CLAIM_MANAGER' && (
          <List
            component="div"
            subheader={
              <ListSubheader component="div" disableSticky>
                {t('sidebar.claims_manager_section')}
              </ListSubheader>
            }
          >
            <SubMenuWrapper>
              <List component="div">
              {role === 'ADMIN' && (
                <ListItem component="div" id="managers">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/managers"
                    startIcon={
                      <ManageAccountsIcon style={{ color: theme.colors.primary.main }} />
                    }
                  >
                    {t('sidebar.managers')}
                  </Button>
                </ListItem>
              )}

              {(role === 'ADMIN' || role === 'MANAGER') && (
                <ListItem component="div" id="responsible">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/responsibles"
                    startIcon={<PersonIcon style={{ color: theme.colors.primary.main }} />}
                  >
                    {t('sidebar.responsibles')}
                  </Button>
                </ListItem>
              )}
              {role !== 'CLAIM_MANAGER' && canClaims && (
                <ListItem component="div" id="claimmanager">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/ClaimManagers"
                    startIcon={<GroupIcon style={{ color: theme.colors.primary.main }} />}
                  >
                    {t('sidebar.claim_managers')}
                  </Button>
                </ListItem>
              )}
              </List>
            </SubMenuWrapper>
          </List>
        )}

        {(canStaff || canStaffReviews) && (
        <List
            component="div"
            subheader={
              <ListSubheader component="div" disableSticky>
                {t('sidebar.collaborations')}
              </ListSubheader>
            }
          >
            <SubMenuWrapper>
              <List component="div">
                {role === 'ADMIN' && canStaff && (
                  <ListItem component="div" id="collaborationssent">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/SentCollab"
                    startIcon={<WorkspacesIcon style={{ color: theme.colors.primary.main }} />}
                  >
                    {t('sidebar.collaborations')}
                    {totalUnread > 0 && (
                      <Box
                        component="span"
                        sx={{
                          ml: 1.5,
                          background: '#18e68e',
                          color: '#000',
                          borderRadius: '10px',
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          minWidth: 20,
                          height: 20,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          px: 0.8
                        }}
                      >
                        {totalUnread}
                      </Box>
                    )}
                  </Button>
                </ListItem>
              )}
              {role === 'ADMIN' && canStaff && (
                <ListItem component="div" id="collaborationsReceived">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/ReceivedCollab"
                    startIcon={
                      <MarkEmailUnreadRoundedIcon
                        style={{ color: theme.colors.primary.main }}
                      />
                    }
                  >
                    {t('sidebar.received')}
                    {pendingCollabCount > 0 && (
                      <Box
                        component="span"
                        sx={{
                          ml: 1.5,
                          background: '#18e68e',
                          color: '#000',
                          borderRadius: '10px',
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          minWidth: 20,
                          height: 20,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          px: 0.8
                        }}
                      >
                        {pendingCollabCount}
                      </Box>
                    )}
                  </Button>
                </ListItem>
              )}
              {canStaffReviews && (
              <>
              <ListItem onClick={handleClick2}>
                <Box display="flex" alignItems="center" width="100%">
                  <Button
                    sx={{
                      justifyContent: 'flex-start',
                      flex: 1,
                      minWidth: 0
                    }}
                    startIcon={<StarRoundedIcon style={{ color: theme.colors.primary.main }} />}
                  >
                    <Box
                      component="span"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0
                      }}
                    >
                      {t('sidebar.staff_ratings')}
                    </Box>
                  </Button>
                  {open2 ? (
                    <ExpandLessIcon style={{ color: theme.colors.primary.main }} />
                  ) : (
                    <ExpandMoreIcon style={{ color: theme.colors.primary.main }} />
                  )}
                </Box>
              </ListItem>
              <Collapse in={open2} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  <ListItem sx={{ pl: 6 }} component="div" id="staff-ratings">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/management/staff-ratings"
                      end
                      startIcon={
                        <PersonIcon sx={{ ml: 2, color: theme.colors.primary.main }} />
                      }
                      sx={{
                        pl: 5,
                        textTransform: 'none',
                        fontWeight: 'bold',
                        color: theme.sidebar.ink[100],
                        justifyContent: 'flex-start',
                        width: '100%',
                        minWidth: 0
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {t('sidebar.staff_ratings_clients')}
                      </Box>
                    </Button>
                  </ListItem>
                  <ListItem sx={{ pl: 6 }} component="div" id="staff-ratings-admin">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/management/staff-ratings/admin"
                      startIcon={
                        <AdminPanelSettingsIcon sx={{ ml: 2, color: theme.colors.primary.main }} />
                      }
                      sx={{
                        pl: 5,
                        textTransform: 'none',
                        fontWeight: 'bold',
                        color: theme.sidebar.ink[100],
                        justifyContent: 'flex-start',
                        width: '100%',
                        minWidth: 0
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {t('sidebar.staff_ratings_admin')}
                      </Box>
                    </Button>
                  </ListItem>
                </List>
              </Collapse>
              </>
              )}
              {role === 'ADMIN' && canStaff && (
                <ListItem component="div" id="attendance">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/attendance"
                    startIcon={<QrCode2Icon style={{ color: theme.colors.primary.main }} />}
                  >
                    {t('sidebar.attendance')}
                  </Button>
                </ListItem>
              )}
              {role === 'ADMIN' && canStaff && (
                <ListItem component="div" id="collaborationsjobs">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/JobPosts"
                    startIcon={<WorkIcon style={{ color: theme.colors.primary.main }} />}
                  >
                    {t('sidebar.jobs')}
                  </Button>
                </ListItem>
              )}
              {role === 'ADMIN' && canStaff && (
                <ListItem component="div" id="available-staff">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/AvailableStaff"
                    startIcon={<PersonAddAlt1Icon style={{ color: theme.colors.primary.main }} />}
                  >
                    {t('sidebar.available_staff')}
                  </Button>
                </ListItem>
              )}

              {role === 'ADMIN' && canStaff && (
                <>
                  <ListItem onClick={handleClick1}>
                    <Box display="flex" alignItems="center" width="100%">
                      <Button
                        sx={{
                          pl: 5,
                          textTransform: 'none',
                          fontWeight: 'bold',
                          color: theme.sidebar.ink[100],
                          justifyContent: 'flex-start',
                          flex: 1
                        }}
                        startIcon={
                          <GroupsIcon sx={{ Pl: 1, color: theme.colors.primary.main }} />
                        }
                      >
                        {t('sidebar.staff')}
                        {totalUnread > 0 && (
                          <Box component="span" sx={{
                            ml: 1.5,
                            background: '#18e68e',
                            color: '#000',
                            borderRadius: '10px',
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            minWidth: 20,
                            height: 20,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            px: 0.8,
                          }}>
                            {totalUnread}
                          </Box>
                        )}
                      </Button>
                      {open1 ? (
                        <ExpandLessIcon style={{ color: theme.colors.primary.main }} />
                      ) : (
                        <ExpandMoreIcon style={{ color: theme.colors.primary.main }} />
                      )}
                    </Box>
                  </ListItem>
                  <Collapse in={open1} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      <ListItem sx={{ pl: 6 }} component="div" id="ListStaff">
                        <Button
                          disableRipple
                          component={RouterLink}
                          onClick={closeSidebar}
                          to="/management/staff"
                          end
                          startIcon={
                            <ListAltIcon sx={{ ml: 2, color: theme.colors.primary.main }} />
                          }
                          sx={{
                            pl: 5,
                            textTransform: 'none',
                            fontWeight: 'bold',
                            color: theme.sidebar.ink[100],
                            justifyContent: 'flex-start',
                            width: '100%'
                          }}
                        >
                          {t('sidebar.list_of_staff')}
                        </Button>
                      </ListItem>
                      <ListItem sx={{ pl: 4 }} component="div" id="messenger">
                        <Button
                          disableRipple
                          component={RouterLink}
                          onClick={closeSidebar}
                          to="/management/staff/messenger"
                          startIcon={
                            <ForumIcon sx={{ ml: 2, color: theme.colors.primary.main }} />
                          }
                          sx={{
                            pl: 5,
                            textTransform: 'none',
                            fontWeight: 'bold',
                            color: theme.sidebar.ink[100],
                            justifyContent: 'flex-start',
                            width: '100%'
                          }}
                        >
                          {t('sidebar.messenger')}
                          {totalUnread > 0 && (
                            <Box component="span" sx={{
                              ml: 1.5, background: '#18e68e', color: '#000',
                              borderRadius: '10px', fontSize: '0.65rem',
                              fontWeight: 700, minWidth: 20, height: 20,
                              display: 'inline-flex', alignItems: 'center',
                              justifyContent: 'center', px: 0.8
                            }}>
                              {totalUnread}
                            </Box>
                          )}
                        </Button>
                      </ListItem>
                      <ListItem sx={{ pl: 4 }} component="div" id="staff-requests">
                        <Button
                          disableRipple
                          component={RouterLink}
                          onClick={closeSidebar}
                          to="/management/staff/requests"
                          startIcon={
                            <AssignmentLateIcon sx={{ ml: 2, color: theme.colors.primary.main }} />
                          }
                          sx={{
                            pl: 5,
                            textTransform: 'none',
                            fontWeight: 'bold',
                            color: theme.sidebar.ink[100],
                            justifyContent: 'flex-start',
                            width: '100%'
                          }}
                        >
                          {t('sidebar.staff_requests')}
                          {pendingRequests > 0 && (
                            <Box component="span" sx={{
                              ml: 1.5, background: '#ff9800', color: '#000',
                              borderRadius: '10px', fontSize: '0.65rem',
                              fontWeight: 700, minWidth: 20, height: 20,
                              display: 'inline-flex', alignItems: 'center',
                              justifyContent: 'center', px: 0.8
                            }}>
                              {pendingRequests}
                            </Box>
                          )}
                        </Button>
                      </ListItem>
                    </List>
                  </Collapse>
                </>
              )}

              {canStaff && (
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/management/staff/group-chat"
                  startIcon={<ForumIcon sx={{ color: theme.colors.primary.main }} />}
                  sx={{
                    pl: 5,
                    textTransform: 'none',
                    fontWeight: 'bold',
                    color: theme.sidebar.ink[100],
                    justifyContent: 'flex-start',
                    width: '100%'
                  }}
                >
                  {t('sidebar.groupChat')}
                </Button>
              </ListItem>
              )}

            </List>
          </SubMenuWrapper>
        </List>
        )}

        {/* Pack sans le module staff : l'admin voit ce qu'il débloquerait et peut changer
            de pack sur le site guest (seul l'admin peut le faire). */}
        {packLoaded && role === 'ADMIN' && !canStaff && !canStaffReviews && (
          <List
            component="div"
            subheader={
              <ListSubheader component="div" disableSticky>
                {t('sidebar.collaborations')}
              </ListSubheader>
            }
          >
            <Box
              id="staff-locked"
              sx={{
                mx: 2,
                mb: 1,
                p: 2,
                borderRadius: 1,
                border: `1px dashed ${alpha(theme.colors.primary.main, 0.5)}`,
                background: alpha(theme.colors.primary.main, 0.06)
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <LockOutlinedIcon sx={{ fontSize: 18, color: theme.colors.primary.main }} />
                <Box component="span" sx={{ fontWeight: 700, color: theme.sidebar.ink[100] }}>
                  {t('pack_feature.sidebar_title')}
                </Box>
              </Box>
              <Box
                component="ul"
                sx={{
                  m: 0,
                  mb: 1.5,
                  pl: 2.5,
                  fontSize: '0.8rem',
                  color: theme.sidebar.ink[70],
                  '& li': { mb: 0.3 }
                }}
              >
                <li>{t('pack_feature.sidebar_staff')}</li>
                <li>{t('pack_feature.sidebar_attendance')}</li>
                <li>{t('pack_feature.sidebar_jobs')}</li>
                <li>{t('pack_feature.sidebar_ratings')}</li>
              </Box>
              <Button
                fullWidth
                size="small"
                variant="contained"
                href={`${process.env.REACT_APP_ADMIN_URL}/abonnements`}
                onClick={closeSidebar}
                sx={{
                  justifyContent: 'center',
                  background: '#18E68E',
                  color: '#000',
                  fontWeight: 700,
                  '&:hover': { background: '#14c97b' }
                }}
              >
                {t('pack_feature.sidebar_upgrade')}
              </Button>
            </Box>
          </List>
        )}

        <List
          component="div"
          subheader={
            <ListSubheader component="div" disableSticky>
              {t('sidebar.account')}
            </ListSubheader>
          }
        >
          <SubMenuWrapper>
            <List component="div">
              {(role === 'ADMIN' || role === 'MANAGER') && (
                <ListItem component="div" id="support">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/support"
                    startIcon={
                      <SupportAgentIcon style={{ color: theme.colors.primary.main }} />
                    }
                  >
                    {t('sidebar.support')}
                  </Button>
                </ListItem>
              )}
              <ListItem component="div">
                <Button
                  onClick={handleLogout}
                  startIcon={<LogoutIcon style={{ color: theme.colors.primary.main }} />}
                >
                  {t('sidebar.exit')}
                </Button>
              </ListItem>
            </List>
          </SubMenuWrapper>
        </List>
      </MenuWrapper>
    </>
  );
}

export default SidebarMenu;
